import { Routes, Route, useNavigate, useLocation, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  LayoutDashboard,
  Users,
  FolderKanban,
  CheckSquare,
  Wallet,
  Target,
  CalendarDays,
  UserPlus,
  Image,
  LogOut,
  ChevronLeft,
  Menu,
  X,
} from 'lucide-react';
import { getCurrentUser, logout } from '@services/auth/index.js';
import { tryAcceptPendingInvitation } from '@services/auth/invite.js';
import { getSignedUrl } from '@services/storage/index.js';
import Logo from '../components/Logo';
import { getCurrentUserProfile } from '@services/db/index.js';
import DashboardPage from '../pages/DashboardPage';
import ClientesPage from '../pages/ClientesPage';
import ClienteDetailPage from '../pages/ClienteDetailPage';
import ProjetosPage from '../pages/ProjetosPage';
import TrafegoPagoPage from '../pages/TrafegoPagoPage';
import ConteudoPage from '../pages/ConteudoPage';
import AgendaPage from '../pages/AgendaPage';
import EquipePage from '../pages/EquipePage';
import PerfilPage from '../pages/PerfilPage';
import TarefasPage from '../pages/TarefasPage';
import FinanceiroPage from '../pages/FinanceiroPage';

const NAV_ITEMS = [
  { href: '/app', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { href: '/app/clientes', label: 'Clientes', icon: Users },
  { href: '/app/projetos', label: 'Projetos', icon: FolderKanban },
  { href: '/app/trafego-pago', label: 'Tráfego Pago', icon: Target },
  { href: '/app/conteudo', label: 'Conteúdo', icon: Image },
  { href: '/app/tarefas', label: 'Tarefas', icon: CheckSquare },
  { href: '/app/agenda', label: 'Agenda', icon: CalendarDays },
  { href: '/app/equipe', label: 'Equipe', icon: UserPlus },
  { href: '/app/financeiro', label: 'Financeiro', icon: Wallet },
];

export default function AuthLayout() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    const currentUser = await getCurrentUser();
    if (!currentUser) {
      navigate('/auth/login');
      return;
    }

    setUser(currentUser);
    let profileResult = await getCurrentUserProfile();

    // Se acabou de confirmar o e-mail depois de aceitar um convite, ainda
    // não tem profile. Termina o convite agora, automaticamente.
    if (!profileResult.data) {
      const pendingToken = localStorage.getItem('on_digital_pending_invite_token');
      if (pendingToken) {
        const acceptResult = await tryAcceptPendingInvitation(pendingToken);
        if (acceptResult.success) {
          localStorage.removeItem('on_digital_pending_invite_token');
          profileResult = await getCurrentUserProfile();
        }
      }
    }

    setProfile(profileResult.data);
    if (profileResult.data?.avatar_url) {
      const signed = await getSignedUrl(profileResult.data.avatar_url);
      setAvatarUrl(signed.data);
    }
    setLoading(false);
  }

  async function handleLogout() {
    await logout();
    navigate('/auth/login');
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background text-text-secondary">
        Carregando...
      </div>
    );
  }

  // Conta criada, mas sem vínculo com nenhuma agência (ex: cadastro direto
  // sem convite). Em vez de mostrar um painel vazio, explica o que fazer.
  if (!profile?.organization_id) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="card max-w-md w-full text-center space-y-4">
          <Logo height={40} />
          <h1 className="text-h2 text-text-primary">Você ainda não faz parte de uma agência</h1>
          <p className="text-sm text-text-secondary">
            Sua conta ({user?.email}) foi criada, mas ainda não está vinculada a nenhuma equipe. Peça a quem
            administra a agência para te enviar um convite e abra o link recebido. Depois disso, você entra
            automaticamente.
          </p>
          <div className="flex gap-3 pt-2">
            <button onClick={() => window.location.reload()} className="btn-secondary flex-1">
              Já aceitei, atualizar
            </button>
            <button onClick={handleLogout} className="btn-primary flex-1">
              Sair
            </button>
          </div>
        </div>
      </div>
    );
  }

  const initials = (profile?.full_name || user?.email || '?')
    .split(' ')
    .map((part: string) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Overlay para mobile quando o menu está aberto */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-ink/50 z-30 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <Sidebar
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed((v) => !v)}
        mobileOpen={mobileMenuOpen}
        onCloseMobile={() => setMobileMenuOpen(false)}
        profile={profile}
        avatarUrl={avatarUrl}
        user={user}
        initials={initials}
        onLogout={handleLogout}
      />

      {/* Main content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b border-border-subtle px-4 sm:px-6 py-4 flex items-center gap-3">
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-surface-muted text-text-secondary"
            aria-label="Abrir menu"
          >
            <Menu size={20} />
          </button>
          <PageTitle />
        </header>

        {/* Routes */}
        <div className="flex-1 overflow-auto p-4 sm:p-6">
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/clientes" element={<ClientesPage />} />
            <Route path="/clientes/:id" element={<ClienteDetailPage />} />
            <Route path="/projetos" element={<ProjetosPage />} />
            <Route path="/trafego-pago" element={<TrafegoPagoPage />} />
            <Route path="/conteudo" element={<ConteudoPage />} />
            <Route path="/agenda" element={<AgendaPage />} />
            <Route path="/equipe" element={<EquipePage />} />
            <Route path="/perfil" element={<PerfilPage />} />
            <Route path="/tarefas" element={<TarefasPage />} />
            <Route path="/financeiro" element={<FinanceiroPage />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

function PageTitle() {
  const location = useLocation();
  if (location.pathname.startsWith('/app/perfil')) {
    return <h1 className="text-h2 text-text-primary">Meu perfil</h1>;
  }
  const found = NAV_ITEMS.find((item) =>
    item.end ? location.pathname === item.href : location.pathname.startsWith(item.href)
  );
  return <h1 className="text-h2 text-text-primary">{found?.label ?? 'Dashboard'}</h1>;
}

function Sidebar({
  collapsed,
  onToggleCollapse,
  mobileOpen,
  onCloseMobile,
  profile,
  avatarUrl,
  user,
  initials,
  onLogout,
}: {
  collapsed: boolean;
  onToggleCollapse: () => void;
  mobileOpen: boolean;
  onCloseMobile: () => void;
  profile: any;
  avatarUrl: string | null;
  user: any;
  initials: string;
  onLogout: () => void;
}) {
  return (
    <aside
      className={`
        group
        fixed lg:static inset-y-0 left-0 z-40
        ${collapsed ? 'lg:w-20' : 'lg:w-64'} w-64
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        bg-ink text-white flex flex-col shrink-0
        transition-[transform,width] duration-200
        relative overflow-hidden
      `}
    >
      {/* Efeito roxo sutil na lateral, ancorado no canto superior */}
      <div
        className="pointer-events-none absolute -top-24 -left-24 w-72 h-72 rounded-full bg-brand-600/30 blur-3xl"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute inset-y-0 left-0 w-px bg-gradient-to-b from-brand-400/60 via-brand-600/20 to-transparent"
        aria-hidden="true"
      />

      <div
        className={`relative flex items-center h-16 border-b border-white/10 ${
          collapsed ? 'justify-center px-4' : 'justify-start pl-6 pr-4'
        }`}
      >
        {collapsed ? (
          <button
            onClick={onToggleCollapse}
            className="shrink-0 rounded-lg hover:bg-white/10 transition-colors p-1"
            aria-label="Expandir menu"
            title="Expandir menu"
          >
            <img
              src="/logo-icon-white.png"
              alt="ON Digital"
              className="object-contain"
              style={{ width: 32, height: 32 }}
            />
          </button>
        ) : (
          <img
            src="/logo-lockup-white.png"
            alt="ON Digital"
            className="shrink-0 object-contain"
            style={{ height: 34, width: 'auto' }}
          />
        )}

        {/* Fechar (mobile): sempre visível, não depende de hover (touch não tem hover) */}
        <button
          onClick={onCloseMobile}
          className="lg:hidden absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-white/10 text-zinc-400"
          aria-label="Fechar menu"
        >
          <X size={18} />
        </button>

        {/* Recolher (desktop): só existe quando expandida, some por padrão, aparece no hover */}
        {!collapsed && (
          <button
            onClick={onToggleCollapse}
            className="hidden lg:flex absolute top-1/2 -translate-y-1/2 right-3 p-1 rounded bg-ink hover:bg-white/10 text-zinc-400 opacity-0 group-hover:opacity-100 transition-opacity duration-150"
            aria-label="Recolher menu"
          >
            <ChevronLeft size={16} />
          </button>
        )}
      </div>

      <nav className="relative flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV_ITEMS.map((item) => (
          <NavItem key={item.href} {...item} collapsed={collapsed} />
        ))}
      </nav>

      <div className="relative px-3 py-4 border-t border-white/10">
        <Link
          to="/app/perfil"
          title="Meu perfil"
          className={`flex items-center gap-3 mb-3 rounded-lg hover:bg-white/5 transition-colors p-1 -m-1 ${
            collapsed ? 'justify-center' : ''
          }`}
        >
          <div className="w-8 h-8 rounded-full bg-brand-400/30 border border-brand-400/40 flex items-center justify-center text-xs font-semibold shrink-0 overflow-hidden">
            {avatarUrl ? (
              <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              initials
            )}
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">
                {profile?.full_name || user?.email?.split('@')[0] || 'Usuário'}
              </p>
            </div>
          )}
        </Link>
        <button
          onClick={onLogout}
          className={`flex items-center gap-2 w-full px-3 py-2 text-sm text-zinc-300 hover:bg-white/10 hover:text-white rounded-lg transition-colors ${
            collapsed ? 'justify-center' : ''
          }`}
        >
          <LogOut size={16} />
          {!collapsed && 'Sair'}
        </button>
      </div>
    </aside>
  );
}

function NavItem({
  href,
  label,
  icon: Icon,
  end,
  collapsed,
}: {
  href: string;
  label: string;
  icon: any;
  end?: boolean;
  collapsed: boolean;
}) {
  const location = useLocation();
  const isActive = end ? location.pathname === href : location.pathname.startsWith(href);

  return (
    <Link
      to={href}
      className={`
        flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
        ${collapsed ? 'justify-center' : ''}
        ${
          isActive
            ? 'bg-brand-600/25 text-white'
            : 'text-zinc-400 hover:bg-white/5 hover:text-white'
        }
      `}
      title={collapsed ? label : undefined}
    >
      <Icon size={18} className={isActive ? 'text-brand-400' : ''} />
      {!collapsed && <span className="truncate">{label}</span>}
    </Link>
  );
}

